import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Square, Loader2, ArrowRight, AlertCircle, ChevronDown, ChevronUp } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const M = {
  gold: "#E4B23C",
  goldSoft: "rgba(228,178,60,0.12)",
  goldLine: "rgba(228,178,60,0.35)",
  panel: "var(--bc-panel, #09090B)",
  elev: "var(--bc-panel-elev, #141416)",
  border: "var(--bc-border, rgba(255,255,255,0.07))",
  text: "var(--bc-ink, #FFFFFF)",
  muted: "var(--bc-sub, #71717A)",
  green: "#22C55E",
  red: "#EF4444",
};
const mono = { fontFamily: "'JetBrains Mono',monospace" };

export default function MultiMonolitePanel({ date }) {
  const { user } = useAuth();
  const isOwner = !!user?.is_owner;
  const [state, setState] = useState(null);
  const [open, setOpen] = useState(false);
  const [limit, setLimit] = useState("");
  const [error, setError] = useState(null);
  const [starting, setStarting] = useState(false);

  const load = useCallback(async () => {
    if (!isOwner || !date) return null;
    try {
      const res = await axios.get(`${API}/admin/monolite/multi`, { params: { date } });
      setState(res.data);
      return res.data?.status;
    } catch (err) {
      setError(err?.response?.data?.detail || "Caricamento MultiMonolite fallito");
      return null;
    }
  }, [isOwner, date]);

  useEffect(() => { setError(null); load(); }, [load]);

  const running = state?.status === "running";

  useEffect(() => {
    if (!running) return;
    const poll = setInterval(() => { load(); }, 6000);
    return () => clearInterval(poll);
  }, [running, load]);

  const start = async () => {
    setStarting(true); setError(null);
    setOpen(true);
    try {
      await axios.post(`${API}/admin/monolite/multi`, {
        date, limit: Number(limit) > 0 ? Number(limit) : undefined,
      });
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || "Avvio scansione fallito");
    } finally {
      setStarting(false);
    }
  };

  if (!isOwner) return null;

  const picks = state?.picks || [];
  const target = state?.limit || state?.default_limit || 25;

  return (
    <div className="max-w-7xl mx-auto px-4 pt-4" data-testid="multimonolite-block">
      <div style={{
        background: M.panel, border: `1px solid ${M.border}`,
        borderLeft: `3px solid ${M.gold}`, padding: open ? "1rem 1.1rem" : "0.6rem 1.1rem",
        transition: "padding 0.2s ease",
      }}>
        <div
          data-testid="multimonolite-toggle"
          onClick={() => setOpen((v) => !v)}
          style={{ display: "flex", alignItems: "center", gap: "0.7rem", flexWrap: "wrap", cursor: "pointer" }}
        >
          <Square size={14} strokeWidth={3} style={{ color: M.gold }} />
          <span style={{
            fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700, fontSize: "0.95rem",
            letterSpacing: "0.2em", textTransform: "uppercase", color: M.gold,
          }}>
            MultiMonolite
          </span>
          {picks.length > 0 && (
            <span data-testid="multimonolite-count-badge" style={{
              ...mono, fontSize: "0.6rem", letterSpacing: "0.08em",
              padding: "0.1rem 0.4rem", color: M.gold,
              border: `1px solid ${M.goldLine}`, background: M.goldSoft,
            }}>
              {picks.length} PICK
            </span>
          )}
          {running && (
            <span data-testid="multimonolite-header-progress" style={{ ...mono, fontSize: "0.6rem", color: M.gold }}>
              <Loader2 size={10} className="animate-spin" style={{ display: "inline", marginRight: 4 }} />
              {state.analyzed}/{state.total || target}
            </span>
          )}
          {open && (
            <span style={{ ...mono, fontSize: "0.62rem", color: M.muted, letterSpacing: "0.08em" }}>
              solo le pick di giornata che superano la soglia · aff. ≥ {state?.min_confidence_threshold ?? 78} · quota ≥ {state?.min_odds_threshold ?? 1.3}
            </span>
          )}
          <span style={{ marginLeft: "auto", color: M.muted, display: "inline-flex" }}>
            {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </span>
        </div>

        {open && (
        <div style={{ display: "flex", alignItems: "center", gap: "0.7rem", flexWrap: "wrap", marginTop: "0.8rem" }}>
          <label style={{
            display: "inline-flex", alignItems: "center", gap: "0.4rem",
            ...mono, fontSize: "0.6rem", letterSpacing: "0.1em",
            textTransform: "uppercase", color: M.muted,
          }}>
            Partite
            <input
              type="number" min={1} max={state?.max_limit || 40}
              value={limit} placeholder={String(state?.default_limit || 25)}
              disabled={running}
              onChange={(e) => setLimit(e.target.value)}
              data-testid="multimonolite-limit-input"
              style={{
                width: 56, background: M.elev, border: `1px solid ${M.border}`,
                color: M.gold, ...mono, fontSize: "0.72rem",
                padding: "0.2rem 0.3rem", textAlign: "center",
              }}
            />
          </label>

          <button
            data-testid="multimonolite-start-btn"
            onClick={start}
            disabled={running || starting}
            style={{
              display: "inline-flex", alignItems: "center", gap: "0.4rem",
              background: M.goldSoft, border: `1px solid ${M.goldLine}`, color: M.gold,
              padding: "0.45rem 0.9rem", cursor: running || starting ? "wait" : "pointer",
              ...mono, fontSize: "0.64rem", letterSpacing: "0.14em", textTransform: "uppercase",
              transition: "background-color 0.2s ease",
            }}
          >
            {running || starting ? <Loader2 size={12} className="animate-spin" /> : <Square size={11} strokeWidth={3} />}
            {running ? "Scansione in corso" : picks.length || state?.status === "done" ? "Rilancia scansione" : "Lancia scansione"}
          </button>
        </div>
        )}

        {open && (
        <>
        {running && (
          <div data-testid="multimonolite-progress" style={{ marginTop: "0.8rem" }}>
            <div style={{ ...mono, fontSize: "0.66rem", color: M.gold, marginBottom: "0.35rem" }}>
              {state.analyzed}/{state.total || target} partite analizzate · {picks.length} pick trovate · {state.elapsed_s || 0}s
            </div>
            <div style={{ height: 3, background: M.border }}>
              <div style={{
                height: "100%", background: M.gold,
                width: `${Math.min(100, ((state.analyzed || 0) / (state.total || target)) * 100)}%`,
                transition: "width 0.4s ease",
              }} />
            </div>
          </div>
        )}

        {(error || state?.error) && !running && (
          <div data-testid="multimonolite-error" style={{
            marginTop: "0.7rem", display: "flex", alignItems: "center", gap: "0.4rem",
            color: M.red, ...mono, fontSize: "0.7rem",
          }}>
            <AlertCircle size={12} /> {error || state.error}
          </div>
        )}

        {!running && state?.status === "done" && picks.length === 0 && (
          <p data-testid="multimonolite-empty" style={{
            margin: "0.7rem 0 0", ...mono, fontSize: "0.7rem", color: M.muted,
          }}>
            Nessuna partita ha superato la soglia su {state.total || 0} analizzate ({state.no_bets || 0} NO BET).
          </p>
        )}

        {picks.length > 0 && (
          <div data-testid="multimonolite-results" style={{
            marginTop: "0.9rem", display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: "0.6rem",
          }}>
            {picks.map((p) => (
              <Link
                key={p.fixture_id}
                to={`/match/${p.fixture_id}`}
                data-testid={`multimonolite-pick-${p.fixture_id}`}
                style={{
                  display: "block", background: M.elev,
                  border: `1px solid ${M.goldLine}`, borderLeft: `2px solid ${M.gold}`,
                  padding: "0.7rem 0.8rem", textDecoration: "none",
                  transition: "border-color 0.2s ease, background-color 0.2s ease",
                }}
              >
                <div style={{ display: "flex", alignItems: "baseline", gap: "0.5rem", flexWrap: "wrap" }}>
                  <span style={{
                    fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700,
                    fontSize: "0.85rem", textTransform: "uppercase", letterSpacing: "0.05em",
                    color: M.text,
                  }}>
                    {p.home} — {p.away}
                  </span>
                  <span style={{ ...mono, fontSize: "0.56rem", color: M.muted }}>
                    {p.league} · {p.kick_off ? new Date(p.kick_off).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" }) : ""}
                  </span>
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: "0.8rem", marginTop: "0.45rem", flexWrap: "wrap" }}>
                  <span style={{
                    ...mono, fontSize: "0.86rem", fontWeight: 700, color: M.gold,
                  }}>{p.bet}</span>
                  <span style={{ ...mono, fontSize: "0.62rem", color: M.green }}>
                    aff. {Math.round(p.confidence_score || 0)}/100
                  </span>
                  <span style={{ ...mono, fontSize: "0.62rem", color: M.muted }}>
                    prob {Number(p.probability || 0).toFixed(0)}% · quota min {Number(p.min_odds || 0).toFixed(2)}
                  </span>
                </div>

                {p.summary && (
                  <p style={{
                    margin: "0.45rem 0 0", fontSize: "0.74rem",
                    color: "var(--bc-ink, #D4D4D8)", lineHeight: 1.5,
                  }}>
                    {p.summary}
                  </p>
                )}

                <span style={{
                  display: "inline-flex", alignItems: "center", gap: "0.3rem",
                  marginTop: "0.5rem", ...mono, fontSize: "0.58rem",
                  letterSpacing: "0.12em", textTransform: "uppercase", color: M.gold,
                }}>
                  Apri partita <ArrowRight size={10} />
                </span>
              </Link>
            ))}
          </div>
        )}
        </>
        )}
      </div>
    </div>
  );
}
